import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, ReactNode, useContext, useEffect, useRef } from "react";
import { useAuthContext } from "../Auth";
import { instance } from "../../api/apiBase";
import { type Notification } from "views/boom-notifications/types";

const NotificationContext = createContext<{
  newestNotifications: Notification[] | undefined;
  totalUnread: number;
  refetchTotalUnreadNotification: () => void;
}>({
  newestNotifications: [],
  totalUnread: 0,
  refetchTotalUnreadNotification: () => {}
});

const notificationApi = {
  getNewest: () =>
    instance.get("/portal/v1/notifications/newest").then((res) => res.data),
  getTotalUnread: () =>
    instance
      .get("/portal/v1/notifications/unread_count")
      .then((res) => res.data?.total_unread || 0)
};

// One string per feed state: a new row or a row read elsewhere both change it.
const feedSignature = (notifications: Notification[]) =>
  notifications.map((n) => `${n.id}:${n.read_at ?? ""}`).join(",");

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { auth } = useAuthContext();
  const queryClient = useQueryClient();

  const { data: totalUnread, refetch: refetchTotalUnreadNotification } =
    useQuery({
      queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"],
      queryFn: () => notificationApi.getTotalUnread(),
      enabled: Boolean(auth)
    });

  const { data: newestNotifications } = useQuery<Notification[]>({
    queryKey: ["NEWEST_NOTIFICATIONS"],
    queryFn: () => notificationApi.getNewest(),
    refetchInterval: 30000,
    enabled: Boolean(auth)
  });

  // This 30s poll is the only "something changed" signal the portal has for
  // notifications. The Notifications page keeps its own per-tab unread counts
  // and list (UNREAD_NOTIFICATIONS_COUNT / NOTIFICATIONS), fetched on mount
  // only, so without this bridge a tab badge stayed stale until the user
  // navigated away and back. Seed on the first load; after that any change in
  // the feed refreshes the page queries and the bell total together.
  const feedSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    if (!newestNotifications) return;

    const signature = feedSignature(newestNotifications);
    const previous = feedSignatureRef.current;
    feedSignatureRef.current = signature;
    if (previous === null || previous === signature) return;

    queryClient.invalidateQueries({ queryKey: ["NOTIFICATIONS"] });
    queryClient.invalidateQueries({ queryKey: ["UNREAD_NOTIFICATIONS_COUNT"] });
    queryClient.invalidateQueries({ queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"] });
  }, [newestNotifications, queryClient]);

  // Task/lead notifications the partner can't reach (CRM off, or the entitlement
  // missing from the plan) are hidden by the API in every endpoint, so the feed
  // and the badge need no client-side filtering.
  return (
    <NotificationContext.Provider
      value={{
        newestNotifications,
        totalUnread,
        refetchTotalUnreadNotification
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotificationContext() {
  return useContext(NotificationContext);
}
